import * as React from 'react'
import { useParams } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AddPeersPerformanceReviewProcessTargetDocument,
  PerformancePeerAssignmentsTargetFragment,
  PerformanceReviewOverviewPeerAssignmentTaskDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'

interface Props {
  processTargetAccess: PerformancePeerAssignmentsTargetFragment
  processTargetEmployeeId: number
  currentPeerEmployeeIds: number[]
  onClose: () => void
}

const AddPeerModal = ({
  processTargetAccess,
  processTargetEmployeeId,
  currentPeerEmployeeIds,
  onClose,
}: Props) => {
  const { reviewId } = useParams()
  const [selectedEmployees, setSelectedEmployees] = React.useState<
    EmployeeForSelectFragmentNoAccess[]
  >([])

  const [addPeersToPerformanceProcess, { loading: adding }] = useMutation(
    AddPeersPerformanceReviewProcessTargetDocument,
    {
      extractorFn: (data) => data.performanceMutations.addPeersPerformanceReviewProcessTarget,
      refetchQueries: [PerformanceReviewOverviewPeerAssignmentTaskDocument],
    }
  )

  const submit = async () => {
    const response = await addPeersToPerformanceProcess({
      variables: {
        peerEmployeeIds: selectedEmployees.map((employee) => employee.id),
        id: `${reviewId}-${processTargetAccess.id}`,
      },
    })

    const result = handleResult(response, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.messages || i18n.t('errors.generic_error'),
      })
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    onClose()
  }

  const handleEmployeeSelection = (employees: EmployeeForSelectFragmentNoAccess[]) => {
    setSelectedEmployees(employees)
  }

  return (
    <Modal
      title={i18n.t('performance_process.reviews.peer_management.assign_peers_to', {
        target_name: processTargetAccess.employeeName?.fullName,
      })}
      illustration="user-male-female"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <Button
          submit
          disabled={!selectedEmployees.length || adding}
          type="primary"
          aria-label={i18n.t('performance_process.reviews.peer_management.assign_peers')}
        >
          {i18n.t('performance_process.reviews.peer_management.assign_peers')}
        </Button>
      }
    >
      <EmployeeSelector
        multiple
        alwaysOpen
        value={selectedEmployees}
        onSelect={handleEmployeeSelection}
        excludeIds={[processTargetEmployeeId, ...currentPeerEmployeeIds]}
      />
    </Modal>
  )
}

export default observer(AddPeerModal)
